import { GUEST_REGISTRATION_STORAGE_KEY } from './constants/guest-registration';
import type { StoredGuestRegistration } from './types';

type Store = Record<string, StoredGuestRegistration>;

/**
 * The same record held in memory, so a browser that blocks web storage still
 * shows the visitor as registered for the page session (NM-2 AC7, design
 * section 41 failure modes). Modelled on `lib/auth/intent-storage.ts`.
 *
 * It is the source of truth only while storage is unusable (`memoryOnly`).
 * Otherwise storage is, so another tab clearing the state also clears it here.
 *
 * Holds only the event id, the email and the token per event (A-305), and
 * nothing in this module logs any of them.
 */
let memoryStore: Store = {};
/** True when the last write to storage failed, so the memory copy stands in for it. */
let memoryOnly = false;

function isStoredRegistration(value: unknown): value is StoredGuestRegistration {
  if (typeof value !== 'object' || value === null) return false;
  const { email, token } = value as Partial<StoredGuestRegistration>;
  return typeof email === 'string' && typeof token === 'string' && token.length > 0;
}

/** Keeps only well-formed entries, so a hand-edited or old value can never reach the page. */
function parseStore(raw: string): Store {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
  // `fromEntries` defines own properties, so an event id like `__proto__` is just a key.
  return Object.fromEntries(
    Object.entries(parsed)
      .filter((pair): pair is [string, StoredGuestRegistration] => isStoredRegistration(pair[1]))
      .map(([eventId, entry]) => [eventId, { email: entry.email, token: entry.token }]),
  );
}

/** Storage while usable, else the in-memory copy. Storage may throw. */
function readStore(): Store {
  if (memoryOnly) return memoryStore;
  try {
    const raw = window.localStorage.getItem(GUEST_REGISTRATION_STORAGE_KEY);
    return raw === null ? {} : parseStore(raw);
  } catch {
    // Blocked storage reads as unavailable; the in-memory copy covers the page session.
    return memoryStore;
  }
}

function writeStore(next: Store): void {
  memoryStore = next;
  try {
    if (Object.keys(next).length === 0) {
      window.localStorage.removeItem(GUEST_REGISTRATION_STORAGE_KEY);
    } else {
      window.localStorage.setItem(GUEST_REGISTRATION_STORAGE_KEY, JSON.stringify(next));
    }
    memoryOnly = false;
  } catch {
    // Quota, private mode or blocked storage: the in-memory copy carries it.
    memoryOnly = true;
  }
}

/**
 * The remembered registration for an event, or null. Safe to call while
 * rendering: it never writes. Never throws.
 */
export function readGuestRegistration(eventId: string): StoredGuestRegistration | null {
  const store = readStore();
  return Object.prototype.hasOwnProperty.call(store, eventId) ? store[eventId] : null;
}

/**
 * Remember a registration for an event, replacing any earlier one for it and
 * leaving other events alone. Never throws; blocked storage keeps it in memory.
 */
export function saveGuestRegistration(eventId: string, entry: StoredGuestRegistration): void {
  writeStore({ ...readStore(), [eventId]: { email: entry.email, token: entry.token } });
}

/** Forget one event's registration, leaving other events alone. Never throws. */
export function clearGuestRegistration(eventId: string): void {
  writeStore(Object.fromEntries(Object.entries(readStore()).filter(([id]) => id !== eventId)));
}

/** Test seam: drops the in-memory copy and the blocked-storage flag between specs. */
export function resetGuestRegistrationMemory(): void {
  memoryStore = {};
  memoryOnly = false;
}
